import { mapActions, mapGetters } from 'vuex';
import messageListEventBus, { BUS_EVENTS } from '~/components/MessageList/messageListEventBus';
import {
  BEGIN_AGENT_CHAT,
  USER_ASKS_TO_TYPE_CHAT,
  CHATBOT_COMMANDS,
  USER_ASK_TRANSACTION_PROBLEM,
} from '~/utils/chat-flags';
import { livechatInfoTagsGenerator } from '~/utils/chat-functions';
import { createMitraChatTrxQueryParam } from '~/utils/mitra-chat';
import Autorefund from '~/mixins/chatbot-functions/autorefund';

export default {
  mixins: [Autorefund],
  computed: {
    ...mapGetters({
      chatbotIntents: 'zopim/message/chatbotIntents',
      getTags: 'zopim/message/getTags',
      botDepartmentId: 'zopim/app/botDepartmentId',
      mitraDepartmentId: 'zopim/app/mitraDepartmentId',
      userTier: 'agent/getUserTier',
      getNeoToggles: 'neo/getNeoToggles',
      allowList: 'allowlist/getList',
    }),
  },
  methods: {
    ...mapActions({
      setChatSession: 'zopim/app/setChatSession',
      sendMessage: 'zopim/message/sendMessage',
      getChatbotIntent: 'zopim/message/getChatbotIntent',
      addTags: 'zopim/message/addTags',
      removeTags: 'zopim/message/removeTags',
      fetchNeoConfigs: 'neo/fetchNeoConfigs',
      openHistoryTransactionPulsaSheet: 'transaction/openHistoryTransactionPulsaSheet'
    }),

    /**
     * Invoked when user clicks an option
     * @param {string} selectedOption - selected option's text
     */
    async onOptionClick(selectedOption = '') {
      try {
        const date = new Date();
        const payload = {
          msg: selectedOption,
          timestamp: date.getTime(),
          nick: 'visitor',
        };

        if (selectedOption.toLocaleLowerCase() === BEGIN_AGENT_CHAT.toLocaleLowerCase()) {
          // Get the agent working hours config
          const {
            data: [config],
          } = await this.fetchNeoConfigs(['csi-config-agent-working-hours']);
          const { start: startWorkingHour, end: endWorkingHour } = config.data || {};

          const currentHour = date.getUTCHours();
          const isWithinAgentWorkingHours = currentHour >= startWorkingHour && currentHour < endWorkingHour;

          // Connect with CS Agent if the user is still in agent working hours
          if (isWithinAgentWorkingHours) {
            messageListEventBus.$emit(BUS_EVENTS.clientTriggerHubungkanDenganCS, payload);
            return;
          }
          // else just send a command, so the user would get some informational messages in return
          this.sendMessage({ ...payload, msg: CHATBOT_COMMANDS.responses.outsideWorkingHours });
          this.$zopim.sendChatMsg(CHATBOT_COMMANDS.responses.outsideWorkingHours);
        } else if (selectedOption === USER_ASKS_TO_TYPE_CHAT) {
          this.sendMessage({ ...payload });
          this.sendMessage({ ...payload, msg: CHATBOT_COMMANDS.shows.showTextField });
          this.$zopim.sendChatMsg(selectedOption);
          this.$zopim.sendChatMsg(CHATBOT_COMMANDS.shows.showTextField);
        } else if (selectedOption.toLocaleLowerCase() === USER_ASK_TRANSACTION_PROBLEM.toLocaleLowerCase()) {
          this.openHistoryTransactionPulsaSheet();
        } else {
          this.sendMessage({ ...payload });
          this.$zopim.sendChatMsg(payload.msg);
          this.trackClickAutorefund(payload.msg);
        }
      } catch (error) {
        console.error('[ERROR] Something when the user clicks chatbot option', error);
      }
    },

    onTransactionClick(transaction) {
      try {
        const date = new Date();
        const detailTrx = transaction.detail;
        const transactionType = transaction.trx_type === 'buka20-transactions'
          ? detailTrx.transaction_type : detailTrx?.transactions[0].type;
        const invoiceNumber = transaction.trx_type === 'buka20-transactions'
          ? detailTrx.invoice_group_number : detailTrx.payment_id;
        const transactionTags = transactionType
          ? [`transaction_type_${transactionType}`]
          : [];
        const invoiceTag = livechatInfoTagsGenerator({
            invoice_id: invoiceNumber,
        });
        const tags = [...transactionTags, ...invoiceTag];
        const message = `Berikut nomor transaksinya ${invoiceNumber}`;
        const command = '[apiresponse] transactionSelected [/apiresponse]';

        const queryParam = createMitraChatTrxQueryParam(transaction);
        // Append query param into current URL
        const currentUrl = new URL(window.location);
        currentUrl.searchParams.set('chat', queryParam);
        window.history.replaceState({}, '', currentUrl);

        this.zChatAddTags(tags);
        this.sendMessage({
            msg: message,
            timestamp: date.getTime(),
            nick: 'visitor',
        });
        this.$zopim.sendChatMsg(message);
        this.sendMessage({
          msg: command,
          timestamp: date.getTime(),
          nick: 'visitor',
        });
        this.$zopim.sendChatMsg(command);
      } catch (error) {
        console.error('[ERROR] Something when the user clicks chatbot option', error);
      }
    },

    /**
     * platinum tier or whitelisted user within agent time range (08.00-22.00) directly go to the agent
     */
    isPlatinumOrWhitelistedEscalationToTier1CsAgent() {
      // Guard against race conditions - ensure data is loaded
      if (!this.userTier || !Array.isArray(this.userTier)) {
        return false;
      }

      if (!this.getNeoToggles || Object.keys(this.getNeoToggles).length === 0) {
        return false;
      }

      let isPlatinumTier = false;
      if (this.getNeoToggles['mitra-chat-platinum-agent-priority-enabled']) {
        isPlatinumTier = this.userTier.includes('mitra_tier_platinum');
      }

      let isWhitelisted = false;
      if (this.getNeoToggles['mitra-chat-whitelist-agent-priority-enabled']) {
        isWhitelisted = this.allowList?.value === 'allowed' || false;
      }

      const now = new Date().toLocaleString('id-ID', { timeZone: 'Asia/Jakarta', hour: '2-digit', minute: '2-digit', hour12: false });
      const [hour, minute] = now.split('.').map(Number);
      const isWithinAgentTimeRange = hour > 8 && (hour < 22 || (hour === 22 && minute === 0));

      return (isPlatinumTier || isWhitelisted) && isWithinAgentTimeRange;
    },

    /**
     * @desc set department
     * @param {Boolean} isChatbot
     */
    zChatSetDepartment({ isChatbot = true }) {
      const depId = isChatbot ? this.botDepartmentId : this.mitraDepartmentId;
      /* istanbul ignore next */
      return new Promise((resolve, reject) => {
        this.$zopim.setVisitorDefaultDepartment(depId, err => {
          if (err) reject(err);
          resolve();
        });
      });
    },

    /**
     * Get all tags in the current Zopim session
     * @returns {string[]}
     */
    zChatGetTags() {
      return this.getTags;
    },

    /**
     * Add tags into to the current Zopim session
     * @param {string[]} tags - tags to be added
     */
    zChatAddTags(tags = []) {
      return new Promise((resolve, reject) => {
        if (tags.length === 0) return resolve();

        // Make sure there are no empty values in the incoming tags array
        const existingTags = this.getTags;
        const sanitizedTags = [...existingTags, ...tags].filter(x => !!x);

        // save tags into the store
        this.addTags({ tags: [...sanitizedTags] });

        // add tags into the current Zopim session
        this.$zopim.addTags([...sanitizedTags], err => {
          if (err) reject(err);
          resolve();
        });
      });
    },

    /**
     * Remove some tags from the current Zopim session
     * @param {string[]} tags - tags to be removed
     */
    zChatRemoveTags(tags) {
      return new Promise((resolve, reject) => {
        this.$zopim.removeTags(tags, err => {
          if (err) reject(err);
          // Remove the tags from the store if the tags is were successfully remove from the current Zopim session
          this.removeTags({ tags });
          resolve();
        });
      });
    },

    /**
     * Remove all tags from the current Zopim session
     */
    zChatRemoveAllTags() {
      return new Promise((resolve, reject) => {
        const tags = this.getTags;
        // Make sure there are no empty values in the tags array
        const sanitizedTags = tags.filter(tag => !!tag);
        if (sanitizedTags.length === 0) return resolve();

        this.$zopim.removeTags([...sanitizedTags], err => {
          if (err) reject(err);
          // Remove the tags from the store if the tags is were successfully remove from the current Zopim session
          this.removeTags({ tags: [...sanitizedTags] });
          resolve();
        });
      });
    },
  },
};
